import { FC, useCallback, useEffect, useState } from 'react';
import { Button, Input, Select, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import GridWrapper from '../../../controls/GridWrapper';
import { DateInput } from '../../../controls/inputs/BaseInput';
import { SearchIcon } from '../../../../libs/corporate/icons/outlined/action/MainCollection';
import styles from '../../NewsChatModal.module.scss';
import { CustomTagProps } from 'rc-select/lib/BaseSelect';
import FilterOutlined from '@ant-design/icons/lib/icons/FilterOutlined';
import UpOutlined from '@ant-design/icons/lib/icons/UpOutlined';
import DownOutlined from '@ant-design/icons/lib/icons/DownOutlined';
import { useForm } from '../../../../system/hooks/useForm';

export interface NewsFiltersValue {
    search: string;
    priorities: number[];
    startDate?: DateTime;
    dueDate?: DateTime;
}

interface Props {
    value: NewsFiltersValue;
    onChange: (value: NewsFiltersValue) => void;
    onReset?: () => void;
}

const PRIORITY_COLORS: Record<number, string> = {
    1: '#29A360',
    2: '#FFC001',
    3: '#FF9900',
    4: '#CC3333',
};


const NewsFilters: FC<Props> = ({ value, onChange }) => {
    const { t } = useTranslation();
    const baseT = (value: string) => t('chat.' + value);
    const [expanded, setExpanded] = useState(false);
    const [searchValue, setSearchValue] = useState(value.search);

    const activeFiltersCount = [
        value.search.trim().length >= 3,
        value.priorities.length > 0,
        value.startDate?.isValid === true,
        value.dueDate?.isValid === true,
    ].filter(Boolean).length;

    const update = <K extends keyof NewsFiltersValue>(
        key: K,
        newValue: NewsFiltersValue[K]
    ) => {
        onChange({
            ...value,
            [key]: newValue,
        });
    };

    const handleReset = () => {
        onChange({
            search: '',
            priorities: [],
            startDate: undefined,
            dueDate: undefined,
        });
    };

    const validateDates = useCallback(
        (
            startDate?: DateTime,
            dueDate?: DateTime
        ): { isValid: boolean; errorKey: string | null } => {
            if (
                startDate?.isValid &&
                dueDate?.isValid &&
                startDate.startOf('day') > dueDate.startOf('day')
            ) {
                return {
                    isValid: false,
                    errorKey: 'startDateAfterDueDate',
                };
            }

            return {
                isValid: true,
                errorKey: null,
            };
        },
        []
    );

    const { errors, setErrors } = useForm<{
        datesRange?: string;
    }>({
        validations: {},
    });

    const updateDate = (
        key: 'startDate' | 'dueDate',
        date?: DateTime
    ) => {
        const nextValue = {
            ...value,
            [key]: date,
        };

        const validation = validateDates(
            nextValue.startDate,
            nextValue.dueDate
        );

        if (!validation.isValid) {
            setErrors(prev => ({
                ...prev,
                datesRange: baseT(validation.errorKey!),
            }));
        } else {
            setErrors(prev => ({
                ...prev,
                datesRange: undefined,
            }));
        }

        onChange(nextValue);
    };

    const getPriorityColor = (priority: number | string): string => {
        return PRIORITY_COLORS[Number(priority)] ?? 'default';
    };

    const tagRender = (props: CustomTagProps) => {
        const { label, value, closable, onClose } = props;

        const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
            event.preventDefault();
            event.stopPropagation();
        };

        return (
            <Tag
                color={getPriorityColor(value)}
                closable={closable}
                onClose={onClose}
                onMouseDown={onPreventMouseDown}
                style={{ marginRight: 3 }}
            >
                {label}
            </Tag>
        );
    };

    const handlePressEnter = () => {
        update('search', searchValue);
    };

    useEffect(() => {
        setSearchValue(value.search);
    }, [value.search]);

    useEffect(() => {
        const timer = setTimeout(() => {
            update('search', searchValue);
        }, 400);

        return () => clearTimeout(timer);
    }, [searchValue]);

    return (
        <>
            <div
                className={styles.filtersHeader}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                <Button className={styles.filtersButton}
                    icon={<FilterOutlined />}
                    onClick={() => setExpanded(prev => !prev)}
                >
                    {baseT('filters')}
                    {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
                    {expanded ? <UpOutlined /> : <DownOutlined />}
                </Button>

                {activeFiltersCount > 0 && (
                    <Button
                        type="link"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleReset();
                        }}
                    >
                        {baseT('clearFilters')}
                    </Button>
                )}
            </div>
            {expanded && (
                <div
                    className={styles.filters}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className={styles.searchBlock}>
                        <Input
                            className="custom-input-withPrefix"
                            style={{ width: '100%', minWidth: 150 }}
                            value={value.search}
                            placeholder={baseT("searchNews")}
                            prefix={<SearchIcon />}
                            onPressEnter={handlePressEnter}
                            onChange={(e) => update('search', e.target.value)}
                            onBlur={(e) => update('search', e.target.value)}
                            allowClear
                        />
                        <Select
                            className={styles.selectFilter}
                            mode="multiple"
                            maxTagCount="responsive"
                            showArrow
                            showSearch
                            placeholder={baseT('priority')}
                            tagRender={tagRender}
                            allowClear
                            optionLabelProp="label"
                            value={value.priorities}
                            onChange={(values) => update('priorities', values)}
                            options={[
                                {
                                    value: 4,
                                    label: baseT('priorityCritical'),
                                },
                                {
                                    value: 3,
                                    label: baseT('priorityHigh'),
                                },
                                {
                                    value: 2,
                                    label: baseT('priorityMedium'),
                                },
                                {
                                    value: 1,
                                    label: baseT('priorityLow'),
                                },
                            ]}
                        />
                    </div>

                    <GridWrapper cols={2}>
                        <DateInput
                            labelVariant="required"
                            inputId={"inputStartDate"}
                            label={baseT('startDate')}
                            value={value.startDate}
                            onBlur={(date) =>
                                updateDate(
                                    'startDate',
                                    date as DateTime | undefined
                                )
                            }
                        />
                        <DateInput
                            labelVariant="required"
                            inputId={"inputDueDate"}
                            label={baseT('dueDate')}
                            value={value.dueDate}
                            onBlur={(date) =>
                                updateDate(
                                    'dueDate',
                                    date as DateTime | undefined
                                )
                            }
                        />
                    </GridWrapper>
                    {errors.datesRange && (
                        <div className={styles.errorMessage}>
                            {errors.datesRange}
                        </div>
                    )}
                </div>
            )}
        </>
    )
};

export default NewsFilters;